import React, { useState, useEffect } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import PaginatedTable from './PaginatedTable';
import { useCache } from '../hooks/useCache';

interface Permission {
  list: boolean;
  download: boolean;
  upload: boolean;
  delete: boolean;
  maxObjectSize: number;
  monthlyDataTransferCap: number;
}

interface BucketTemplate {
  templateId: string;
  name: string;
  permissions: Permission;
  createdAt: string;
  createdBy: string;
}

const BucketTemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<BucketTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<BucketTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<string | null>(null);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    permissions: {
      list: true,
      download: false,
      upload: false,
      delete: false,
      maxObjectSize: 100,
      monthlyDataTransferCap: 10
    }
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await apiCall('/admin/bucket-templates');
      setTemplates(response);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingTemplate) {
      await updateTemplate();
    } else {
      await createTemplate();
    }
  };

  const createTemplate = async () => {
    try {
      await apiCall('/admin/bucket-templates', {
        method: 'POST',
        body: JSON.stringify(newTemplate)
      });
      
      toast.success('Template created successfully');
      resetForm();
      loadTemplates();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const updateTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      await apiCall(`/admin/bucket-templates/${editingTemplate.templateId}`, {
        method: 'PUT',
        body: JSON.stringify(newTemplate)
      });
      
      toast.success('Template updated successfully');
      resetForm();
      loadTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTemplate) return;
    
    try {
      await apiCall(`/admin/bucket-templates/${deletingTemplate}`, {
        method: 'DELETE'
      });
      
      toast.success('Template deleted successfully');
      setDeletingTemplate(null);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
      setDeletingTemplate(null);
    }
  };

  const startEdit = (template: BucketTemplate) => {
    setEditingTemplate(template);
    setNewTemplate({
      name: template.name,
      permissions: { 
        ...template.permissions,
        delete: template.permissions.delete || false // Ensure delete property exists
      }
    });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setShowCreateForm(false);
    setNewTemplate({
      name: '',
      permissions: {
        list: true,
        download: false,
        upload: false,
        delete: false,
        maxObjectSize: 100,
        monthlyDataTransferCap: 10
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Bucket Permission Templates</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          {showCreateForm ? 'Cancel' : 'Create Template'}
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">
            {editingTemplate ? 'Edit Template' : 'Create New Template'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Template Name</label>
              <input
                type="text"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Read Only, Full Access, Upload Only"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'list', label: 'Can List Files' },
                  { key: 'download', label: 'Can Download' },
                  { key: 'upload', label: 'Can Upload' },
                  { key: 'delete', label: 'Can Delete Objects' }
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newTemplate.permissions[key as keyof Permission] as boolean}
                      onChange={(e) => setNewTemplate({
                        ...newTemplate,
                        permissions: {
                          ...newTemplate.permissions,
                          [key]: e.target.checked
                        }
                      })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Object Size (MB)</label>
                <input
                  type="number"
                  value={newTemplate.permissions.maxObjectSize}
                  onChange={(e) => setNewTemplate({
                    ...newTemplate,
                    permissions: {
                      ...newTemplate.permissions,
                      maxObjectSize: parseInt(e.target.value) || 0
                    }
                  })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  placeholder="0 = no limit"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Monthly Data Transfer Cap (GB)</label>
                <input
                  type="number"
                  value={newTemplate.permissions.monthlyDataTransferCap}
                  onChange={(e) => setNewTemplate({
                    ...newTemplate,
                    permissions: {
                      ...newTemplate.permissions,
                      monthlyDataTransferCap: parseInt(e.target.value) || 0
                    }
                  })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  placeholder="0 = no limit"
                />
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map(template => (
          <div key={template.templateId} className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <h4 className="font-medium text-lg text-gray-900">{template.name}</h4>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEdit(template)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => setDeletingTemplate(template.templateId)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex flex-wrap gap-1">
                {template.permissions.list && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    List
                  </span>
                )}
                {template.permissions.download && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                    Download
                  </span>
                )}
                {template.permissions.upload && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    Upload
                  </span>
                )}
                {template.permissions.delete && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    Delete
                  </span>
                )}
              </div>
              
              <div className="text-sm text-gray-600">
                {template.permissions.maxObjectSize > 0 && (
                  <div>Max Size: {template.permissions.maxObjectSize}MB</div>
                )}
                {template.permissions.monthlyDataTransferCap > 0 && (
                  <div>Transfer Cap: {template.permissions.monthlyDataTransferCap}GB/month</div>
                )}
              </div>
            </div>
            
            <div className="text-xs text-gray-500">
              <div>Created: {new Date(template.createdAt).toLocaleDateString()}</div>
              <div>By: {template.createdBy}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Delete Template</h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this template? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setDeletingTemplate(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BucketTemplateManager;